/** @file Teams repository — CRUD + list over users/{uid}/teams. */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { teamsCol, teamDoc } from "./db.js";
import { getDb } from "./firebase.js";

/** @typedef {import("../types/schemas.js").TeamDoc} TeamDoc */

/**
 * @param {string} uid
 * @returns {Promise<TeamDoc[]>}
 */
export async function listTeams(uid) {
  const q = query(teamsCol(uid), orderBy("name", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

/**
 * @param {string} uid
 * @param {string} teamId
 * @returns {Promise<TeamDoc | null>}
 */
export async function getTeam(uid, teamId) {
  const snap = await getDoc(teamDoc(uid, teamId));
  return snap.exists() ? snap.data() : null;
}

/**
 * @param {string} uid
 * @param {Omit<TeamDoc, "id" | "createdAt" | "updatedAt">} input
 * @returns {Promise<string>}
 */
export async function createTeam(uid, input) {
  const db = getDb();
  if (!db) {throw new Error("Firestore not initialized");}
  const ref = await addDoc(collection(db, "users", uid, "teams"), {
    ...input,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * @param {string} uid
 * @param {string} teamId
 * @param {Partial<TeamDoc>} patch
 */
export async function updateTeam(uid, teamId, patch) {
  const db = getDb();
  if (!db) {throw new Error("Firestore not initialized");}
  await updateDoc(doc(db, "users", uid, "teams", teamId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} uid
 * @param {string} teamId
 */
export async function deleteTeam(uid, teamId) {
  await deleteDoc(teamDoc(uid, teamId));
}
